import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { Users, Heart, TrendingUp, MessageCircle, BarChart, type LucideIcon } from 'lucide-react';

// Methodist color palette
const methodistBlue = '#0066CC';

interface Metric {
  label: string;
  value: number;
  change: string;
  trend: 'up' | 'down';
  color: string;
}

interface TrendData {
  label: string;
  value: number;
  color: string;
}

interface MetricType {
  label: string;
  value: number;
  goal: number;
  icon: LucideIcon;
  color: string;
  progress: number;
}

interface MobileCultureMetricsProps {
  metrics: MetricType[];
  score: number;
  trend: string;
  onNavigate: (tab: string) => void;
}

const MobileCultureMetrics: React.FC<MobileCultureMetricsProps> = ({ metrics, score, trend, onNavigate }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <VStack spacing={6} p={4} pt={8} align="stretch">
      {/* Header */}
      <Box bg={methodistBlue} p={4} borderRadius="lg" color="white">
        <Text fontSize="sm">SentientIQ Cultural Quotient</Text>
        <Text fontSize="lg" fontWeight="medium" mt={1}>
          Community Metrics
        </Text>
      </Box>

      {/* Key Metrics Grid */}
      <SimpleGrid columns={2} spacing={4}>
        {metrics.map((metric, index) => (
          <Box
            key={index}
            bg={cardBg}
            p={4}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={2}>
              <HStack color={metric.color}>
                <Icon as={metric.icon} boxSize={5} />
                <Text fontSize="sm" fontWeight="medium">
                  {metric.label}
                </Text>
              </HStack>
              <HStack justify="space-between" width="100%" align="baseline">
                <Text fontSize="2xl" fontWeight="bold">
                  {metric.value}%
                </Text>
                <Badge colorScheme={trend === 'up' ? 'green' : 'red'}>
                  {trend}
                </Badge>
              </HStack>
              <Box w="100%" h="2px" bg="gray.100" position="relative">
                <Box
                  w={`${metric.progress}%`}
                  h="100%"
                  bg={metric.color}
                  position="absolute"
                  top={0}
                  left={0}
                />
              </Box>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Monthly Trends */}
      <Box
        bg={cardBg}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <VStack align="start" spacing={4}>
          <HStack>
            <Icon as={BarChart} boxSize={5} color={methodistBlue} />
            <Text fontSize="sm" fontWeight="medium">
              Monthly Trends
            </Text>
          </HStack>

          <SimpleGrid columns={2} spacing={4} width="100%">
            {[
              { label: 'Group Sessions', value: 24, color: 'blue.500' },
              { label: 'Active Members', value: 156, color: 'green.500' },
              { label: 'Support Hours', value: 48, color: 'purple.500' },
              { label: 'Peer Connections', value: 89, color: 'red.500' },
            ].map((trend, index) => (
              <HStack
                key={index}
                justify="space-between"
                p={3}
                bg={useColorModeValue('gray.50', 'gray.700')}
                borderRadius="md"
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    {trend.label}
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color={trend.color}>
                    {trend.value}
                  </Text>
                </VStack>
                <Icon as={TrendingUp} boxSize={4} color={trend.color} />
              </HStack>
            ))}
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Weekly Summary */}
      <Box
        bg={cardBg}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <VStack align="start" spacing={3}>
          <Text fontSize="sm" fontWeight="medium">
            Weekly Summary
          </Text>
          <VStack align="stretch" spacing={2} width="100%">
            {[
              {
                label: 'Active Group Sessions',
                value: '8 sessions',
                change: '+2 from last week',
              },
              {
                label: 'New Peer Connections',
                value: '24 connections',
                change: '+5 from last week',
              },
              {
                label: 'Support Network Growth',
                value: '15%',
                change: '+3% from last week',
              },
            ].map((item, index) => (
              <HStack
                key={index}
                justify="space-between"
                p={3}
                bg={useColorModeValue('gray.50', 'gray.700')}
                borderRadius="md"
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm">{item.label}</Text>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    {item.change}
                  </Text>
                </VStack>
                <Text fontSize="sm" fontWeight="medium">
                  {item.value}
                </Text>
              </HStack>
            ))}
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
};

export default MobileCultureMetrics;
